import {Stack, Typography} from "@mui/material";
import {PlainTime, TemporalFormat} from "@variocube/app-ui";
import React, {ReactNode} from "react";
import {B2SiteSummary} from "../data/client.gen";
import {getWeekday, parseZoneDateTimeFromDate, Weekday, WEEKDAYS} from "../dates-utils";
import {useLocalization} from "../i18n";
import {isBlank, notBlank} from "../utils";

export const Accessibility = ({site}: { site: B2SiteSummary }) => {
	const {t, e} = useLocalization();

	if (site.alwaysAccessible) return <span>24/7</span>;

	const renderTimeframe = (from?: string, until?: string) => {
		const fromNode = (notBlank(from))
			? <TemporalFormat value={PlainTime.from(from as string)} timeStyle="short" />
			: undefined;
		const untilNode = (notBlank(until))
			? <TemporalFormat value={PlainTime.from(until as string)} timeStyle="short" />
			: undefined;
		return (fromNode !== undefined && untilNode !== undefined)
			? (
				<span>
					{fromNode} - {untilNode}
				</span>
			)
			: (fromNode ?? untilNode ?? "");
	};

	if (notBlank(site.activeFrom) && notBlank(site.activeUntil)) {
		const zoneId = site.zoneId ?? "UTC";
		const duration = parseZoneDateTimeFromDate(site.activeFrom as string, zoneId).until(
			parseZoneDateTimeFromDate(site.activeUntil as string, zoneId),
			{largestUnit: "days"},
		).days + 1;
		if (duration < 7) {
			const weekdays: { weekday: Weekday; date: ReactNode }[] = [];
			for (let i = 0; i < duration; i++) {
				const date = parseZoneDateTimeFromDate(site.activeFrom as string, zoneId).add({days: i});
				const weekday = getWeekday(date.dayOfWeek);
				weekdays.push({
					weekday,
					date: <TemporalFormat value={date.toPlainDate()} />,
				});
			}

			if (weekdays.length === 1) {
				const accessibility = site.accessibility && site.accessibility[weekdays[0].weekday];
				if (accessibility === undefined) {
					return <span />;
				}
				return (
					<span>
						{renderTimeframe(accessibility.from, accessibility.until)}
					</span>
				);
			}

			return (
				<Stack
					direction="column"
					gap={1}
				>
					{weekdays.map(wd => (
						<Typography key={wd.weekday} variant="body2">
							{e("weekdays", wd.weekday)} {wd.date}: {renderTimeframe(
								site.accessibility[wd.weekday]?.from,
								site.accessibility[wd.weekday]?.until,
							)}
						</Typography>
					))}
				</Stack>
			);
		}
	}

	const uniqueTimeframes = Object.values(site.accessibility)
		.map(({from, until}) => `${from} - ${until}`)
		.filter((value, index, self) => self.indexOf(value) === index);

	// Same timeframe across all weekdays
	if (uniqueTimeframes.length === 1) {
		return (
			<span>
				{t("weekdays.Monday")} - {t("weekdays.Sunday")}:{" "}
				{renderTimeframe(site.accessibility["Monday"]?.from, site.accessibility["Monday"]?.until)}
			</span>
		);
	}

	// Different timeframe across all weekdays
	if (uniqueTimeframes.length === 7) {
		return (
			<Stack
				direction="column"
				gap={1}
			>
				{WEEKDAYS.map(wd => (
					<Typography key={wd} variant="body2">
						{e("weekdays", wd)}:{" "}
						{renderTimeframe(site.accessibility[wd]?.from, site.accessibility[wd]?.until)}
					</Typography>
				))}
			</Stack>
		);
	}

	const chunkedMoToFrTimeframes: { [timeframe: string]: string[] } = {};
	const chunkedSaToSuTimeframes: { [timeframe: string]: string[] } = {};
	for (let [weekday, {from, until}] of Object.entries(site.accessibility)) {
		const tfString = `${from} - ${until}`;
		if (weekday !== "Saturday" && weekday !== "Sunday") {
			if (chunkedMoToFrTimeframes.hasOwnProperty(tfString)) chunkedMoToFrTimeframes[tfString].push(weekday);
			else chunkedMoToFrTimeframes[tfString] = [weekday];
		}
		else {
			if (chunkedSaToSuTimeframes.hasOwnProperty(tfString)) chunkedSaToSuTimeframes[tfString].push(weekday);
			else chunkedSaToSuTimeframes[tfString] = [weekday];
		}
	}

	// Linear identically timeframes from weekdays group Mo. - Fr. and Sa. - So. or Mo. - Fr., Sa. and So.
	let hasMoToFrChunk = Object.keys(chunkedMoToFrTimeframes).length === 1
		? Object.keys(chunkedMoToFrTimeframes)[0]
		: undefined;
	let hasSaToSuChunk = Object.keys(chunkedSaToSuTimeframes).length === 1
		? Object.keys(chunkedSaToSuTimeframes)[0]
		: undefined;

	if (hasMoToFrChunk) {
		return (
			<Stack
				direction="column"
				gap={1}
			>
				<Typography variant="body2">
					{t("weekdayShorts.Monday")} - {t("weekdayShorts.Friday")}: {hasMoToFrChunk}
				</Typography>
				{notBlank(hasSaToSuChunk) && (
					<Typography variant="body2">
						{t("weekdayShorts.Saturday")} - {t("weekdayShorts.Sunday")}: {hasSaToSuChunk}
					</Typography>
				)}
				{isBlank(hasSaToSuChunk) && (
					<>
						<Typography variant="body2">
							{t("weekdayShorts.Saturday")}: {renderTimeframe(
								site.accessibility["Saturday"]?.from,
								site.accessibility["Saturday"]?.until,
							)}
						</Typography>
						<Typography variant="body2">
							{t("weekdayShorts.Sunday")}:{" "}
							{renderTimeframe(site.accessibility["Sunday"]?.from, site.accessibility["Sunday"]?.until)}
						</Typography>
					</>
				)}
			</Stack>
		);
	}

	// Non-linear identically timeframe between Mo. - Fr. and Sa. - So.
	let sortedMoToFrChunk: { weekdays: string[]; timeframe: string; timeframeDisplay: ReactNode }[] = [];
	let sortedSaToSuChunk: { weekdays: string[]; timeframe: string; timeframeDisplay: ReactNode }[] = [];
	for (let weekday of WEEKDAYS) {
		for (let [tf, days] of Object.entries(chunkedMoToFrTimeframes)) {
			if (days.includes(weekday) && !sortedMoToFrChunk.find(c => c.timeframe === tf)) {
				sortedMoToFrChunk.push({
					weekdays: days.map(d => e("weekdayShorts", d)),
					timeframe: tf,
					timeframeDisplay: renderTimeframe(
						site.accessibility[weekday]?.from,
						site.accessibility[weekday]?.until,
					),
				});
			}
		}
		for (let [tf, days] of Object.entries(chunkedSaToSuTimeframes)) {
			if (days.includes(weekday) && !sortedSaToSuChunk.find(c => c.timeframe === tf)) {
				sortedSaToSuChunk.push({
					weekdays: days.map(d => e("weekdayShorts", d)),
					timeframe: tf,
					timeframeDisplay: renderTimeframe(
						site.accessibility[weekday]?.from,
						site.accessibility[weekday]?.until,
					),
				});
			}
		}
	}

	return (
		<Stack
			direction="column"
			gap={1}
		>
			{sortedMoToFrChunk.map((c, i) => (
				<Typography variant="body2" key={"mfChunk-" + i}>
					{c.weekdays.join(", ")}: {c.timeframeDisplay}
				</Typography>
			))}
			{sortedSaToSuChunk.map((c, i) => (
				<Typography variant="body2" key={"ssChunk-" + i}>
					{c.weekdays.join(", ")}: {c.timeframeDisplay}
				</Typography>
			))}
		</Stack>
	);
};
